import { useState, useEffect, useRef, useCallback } from 'react';

const getDefaultDisplayPrefs = () => ({
  clientProfile: true,
  widgetAssessments: true,
  clientSummary: true,
  quickStats: true,
  firmCompliance: true,
  pathways: true,
  actionItems: true,
  missingInfo: true,
  clientFollowUps: true,
  nextSteps: true,
  internalNotes: true,
  professionalAdvice: true,
  clientCommunication: true,
  longTermOutlook: true
});

export const useDisplayPrefs = (axios) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const [displayPrefs, setDisplayPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('caseDashboard_displayPrefs');
      return saved ? { ...getDefaultDisplayPrefs(), ...JSON.parse(saved) } : getDefaultDisplayPrefs();
    } catch {
      return getDefaultDisplayPrefs();
    }
  });

  // Load saved prefs from server on mount (overrides localStorage)
  const displayPrefsLoadedRef = useRef(false);
  useEffect(() => {
    if (displayPrefsLoadedRef.current) return;
    displayPrefsLoadedRef.current = true;
    const loadServerPrefs = async () => {
      try {
        const res = await axios.get('/profile/analysis-display-prefs');
        if (res.data?.analysisDisplayPrefs) {
          const merged = { ...getDefaultDisplayPrefs(), ...res.data.analysisDisplayPrefs };
          setDisplayPrefs(merged);
          localStorage.setItem('caseDashboard_displayPrefs', JSON.stringify(merged));
        }
      } catch {
        // Server prefs not available, keep localStorage/defaults
      }
    };
    loadServerPrefs();
  }, [axios]);

  const savePrefsToServer = useCallback((prefs) => {
    axios.put('/profile/analysis-display-prefs', { analysisDisplayPrefs: prefs }).catch(() => {});
  }, [axios]);

  const updateDisplayPref = (key, value) => {
    const newPrefs = { ...displayPrefs, [key]: value };
    setDisplayPrefs(newPrefs);
    try {
      localStorage.setItem('caseDashboard_displayPrefs', JSON.stringify(newPrefs));
    } catch (e) {
      console.error('Failed to save preferences:', e);
    }
    savePrefsToServer(newPrefs);
  };

  const resetDisplayPrefs = () => {
    const defaults = getDefaultDisplayPrefs();
    setDisplayPrefs(defaults);
    try {
      localStorage.setItem('caseDashboard_displayPrefs', JSON.stringify(defaults));
    } catch (e) {
      console.error('Failed to save preferences:', e);
    }
    savePrefsToServer(defaults);
  };

  return {
    showSettingsModal,
    setShowSettingsModal,
    displayPrefs,
    updateDisplayPref,
    resetDisplayPrefs,
  };
};
